import type {TimeEntry} from '@package/shared'
import {useCallback} from 'react'
import {EditModeButtons} from '../../components/EditModeButtons.tsx'
import type {DateRange} from '../../../../components/dateRangeSelector/DateRangeSelector.tsx'
import type {UpdateTimeEntryRequest} from '@app/api-resolver/router'
import {TimeEntryFormFields} from '../TimeEntryFormFields.tsx'
import {TimeEntryFormValues} from '../TimeEntryFormValues.ts'
import {useForm} from '@mantine/form'
import type {PartialTimeEntryFormValues} from '../PartialTimeEntryFormValues.ts'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props = {
  day: DateRange
  entry: TimeEntry
  onUpdate: (request: UpdateTimeEntryRequest) => Promise<void>
  updating: boolean
  onDelete: (request: TimeEntry) => Promise<void>
  deleting: boolean
  disabled: boolean
}

export const UpdateTimeEntryForm = ({
  day,
  entry,
  onUpdate,
  updating,
  onDelete,
  deleting,
  disabled: containerDisabled,
}: Props) => {
  const form = useForm<PartialTimeEntryFormValues, TimeEntryFormValues | undefined>({
    initialValues: entry satisfies PartialTimeEntryFormValues,
    validate: zod4Resolver(TimeEntryFormValues),
    transformValues: (values) => TimeEntryFormValues.safeParse(values).data,
  })

  const handleSubmit = useCallback(
    async (values: TimeEntryFormValues | undefined) => {
      if (!values) return
      await onUpdate({
        id: entry.id,
        ...values,
      } satisfies UpdateTimeEntryRequest)
    },
    [entry.id, onUpdate],
  )

  const handleDelete = useCallback(async () => {
    await onDelete(entry)
  }, [entry, onDelete])

  const disabled = containerDisabled

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 5}}>
      <TimeEntryFormFields form={form} day={day} disabled={disabled} />

      <EditModeButtons
        submitVariant={'update'}
        submitting={updating}
        destructiveVariant={'delete'}
        destructing={deleting}
        onDestruct={handleDelete}
        disabled={disabled}
      />
    </form>
  )
}
